import type { AppState } from '@protector/entities/__legacy__/redux.types';
import type { MethodologyEntity } from '@protector/entities/methodology/models';
import type { IndicatorDTO } from '@protector/entities/phenomenon/models';
import type {
  GetCurrentSeasonAreas,
  GetSeasonAreasByDate,
  LoadProperties,
  LoadPropertiesAreaInfo,
  LoadPropertyPhenomena,
  LoadPropertyTime,
  LoadSeverityByIndicator
} from '@protector/entities/property/__legacy__/redux.actions';
import {
  GetCurrentSeasonAreasFailure,
  GetCurrentSeasonAreasSuccess,
  GetSeasonAreasByDateFailure,
  GetSeasonAreasByDateSuccess,
  LoadPropertiesAreaInfoSuccess,
  LoadPropertiesFailure,
  LoadPropertiesSuccess,
  LoadPropertyIndicatorsSuccess,
  LoadPropertyMethodologiesSuccess,
  LoadPropertyPhenomenaSuccess,
  LoadPropertyTimeSuccess,
  LoadSeverityByIndicatorSuccess
} from '@protector/entities/property/__legacy__/redux.actions';
import { PropertyActionsTypes } from '@protector/entities/property/__legacy__/redux.constants';
import type {
  CurrentSeasonArea,
  GetCurrentSeasonAreaPayload,
  IndicatorPressurePayload,
  PropertyInfoAddress,
  PropertyTime
} from '@protector/entities/property/models';
import type { Nullable, UUID } from '@protector/utils/types';
import { notification } from 'antd';
import i18n from 'config/i18n';
import { captureError } from 'infra/monitoring/monitoring.utils';
import moment from 'moment';
import type { ActionsObservable, StateObservable } from 'redux-observable';
import { ofType } from 'redux-observable';
import { from, of } from 'rxjs';
import { catchError, concatMap, delay, exhaustMap, flatMap, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { filterSeasonsActive } from '../season/season.functions';
import {
  getCurrentSeasonAreas,
  getIndicatorPressure,
  getIndicatorsByIds,
  getMethodologiesByIds,
  getMethodologyIdsForPropertySeasonAndAreas,
  getPhenomenaByIds,
  getPropertyBasicInfo,
  getPropertyTime,
  getSeasonAreasBySeasons
} from './property.service';

const handlePropertyError = (error: string) => {
  setTimeout(() => {
    notification.error({
      title: i18n.t('general.property_not_allowed')
    });
  }, 2000);
  return of(LoadPropertiesFailure(error)).pipe(delay(5000));
};

export const handleLoadProperties = (action$: ActionsObservable<ReturnType<typeof LoadProperties>>, state$: StateObservable<AppState>) =>
  action$.pipe(
    ofType(PropertyActionsTypes.LOAD_PROPERTIES),
    withLatestFrom(state$.pipe(map(state => state.uiState.auth.isLogged))),
    exhaustMap(([, isLogged]) => {
      if (!isLogged) {
        return of();
      }
      return getPropertyBasicInfo(false).pipe(
        mergeMap(response => {
          const propertyPage = response.data;

          if (!propertyPage?.mutableContent?.length) return from([LoadPropertiesFailure('')]);

          return from([LoadPropertiesSuccess(propertyPage)]);
        }),
        catchError((error: string) => handlePropertyError(error))
      );
    })
  );

export const handleLoadPropertiesAreaInfo = (
  action$: ActionsObservable<ReturnType<typeof LoadPropertiesAreaInfo>>,
  state$: StateObservable<AppState>
) =>
  action$.pipe(
    ofType(PropertyActionsTypes.LOAD_PROPERTIES_AREA_INFO),
    withLatestFrom(state$.pipe(map(state => state.uiState.auth.isLogged))),
    exhaustMap(([, isLogged]) => {
      if (!isLogged) {
        return of();
      }

      return getPropertyBasicInfo(true).pipe(
        mergeMap(response => {
          const propertyPage = response.data;

          if (!propertyPage?.mutableContent?.length) return from([LoadPropertiesFailure('')]);

          return from([LoadPropertiesAreaInfoSuccess(propertyPage)]);
        }),
        catchError(handlePropertyError)
      );
    })
  );

export const handleLoadIndicatorPressure = (action$: ActionsObservable<ReturnType<typeof LoadSeverityByIndicator>>) =>
  action$.pipe(
    ofType(PropertyActionsTypes.LOAD_SEVERITY_BY_INDICATOR),
    map(action => action.payload),
    mergeMap(({ propertyId, methodologyIds, indicatorIds }: IndicatorPressurePayload) => {
      return getIndicatorPressure(propertyId, indicatorIds, methodologyIds).pipe(
        mergeMap(results => {
          if (results) {
            return of(LoadSeverityByIndicatorSuccess(results, indicatorIds[0]));
          }
          return of(LoadSeverityByIndicatorSuccess([], indicatorIds[0]));
        })
      );
    })
  );

const getCustomIndicatorIds = (methodologies: MethodologyEntity[]): UUID[] => [
  ...new Set(
    methodologies.flatMap(methodology =>
      methodology.analytic_context_dto.custom_indicator_dtos.map(customIndicator => customIndicator.indicator_id)
    )
  )
];

const getMethodologyPhenomenonIds = (methodologies: MethodologyEntity[]): UUID[] =>
  methodologies.flatMap(methodology =>
    methodology.inspection_layout.ordered_inspection_groups.flatMap(group =>
      group.ordered_categories.flatMap(category => category.ordered_phenomenons.map(phenomenon => phenomenon.component_id))
    )
  );

const loadPhenomenaForMethodologies = (methodologies: MethodologyEntity[]) =>
  getIndicatorsByIds(getCustomIndicatorIds(methodologies)).pipe(
    mergeMap((indicators: IndicatorDTO[]) => {
      const phenomenonIds: UUID[] = [
        ...new Set([...getMethodologyPhenomenonIds(methodologies), ...indicators.flatMap(indicator => indicator.phenomenon_ids)])
      ];
      return getPhenomenaByIds(phenomenonIds).pipe(
        mergeMap(phenomenons =>
          of(
            LoadPropertyPhenomenaSuccess(phenomenons),
            LoadPropertyIndicatorsSuccess(indicators),
            LoadPropertyMethodologiesSuccess(methodologies)
          )
        )
      );
    })
  );

export const handleLoadPropertyPhenomena = (action$: ActionsObservable<ReturnType<typeof LoadPropertyPhenomena>>) =>
  action$.pipe(
    ofType(PropertyActionsTypes.LOAD_PROPERTY_PHENOMENA),
    map(action => action.payload),
    switchMap(payload => {
      return getMethodologyIdsForPropertySeasonAndAreas(payload?.propertyId, payload?.seasonIds, payload?.areaIds).pipe(
        map((methodologyIds: UUID[]): UUID[] => [...new Set(methodologyIds)]),
        mergeMap((methodologyIds: UUID[]) => getMethodologiesByIds(methodologyIds).pipe(mergeMap(loadPhenomenaForMethodologies)))
      );
    })
  );

export const handleLoadPropertyTime = (action$: ActionsObservable<ReturnType<typeof LoadPropertyTime>>) =>
  action$.pipe(
    ofType(PropertyActionsTypes.LOAD_PROPERTY_TIME),
    map(action => action.payload),
    concatMap((propertyId: string) =>
      getPropertyTime(propertyId).pipe(
        flatMap((property: PropertyTime) => {
          return of(LoadPropertyTimeSuccess(property.currentDateTimeUTC));
        }),
        catchError(error => {
          captureError(error, { errorType: 'AxiosError', domain: 'Property' });
          return of();
        })
      )
    )
  );

const filterCurrentSeasonAreas = (seasonAreas: CurrentSeasonArea[], date: string, timezone: string) => {
  return seasonAreas.filter(
    item => moment.tz(item.endsAt, timezone).isSameOrAfter(moment(date)) && moment.tz(item.startsAt, timezone).isSameOrBefore(moment(date))
  );
};

export const handleGetCurrentSeasonAreas = (
  action$: ActionsObservable<ReturnType<typeof GetCurrentSeasonAreas>>,
  state$: StateObservable<AppState>
) =>
  action$.pipe(
    ofType(PropertyActionsTypes.GET_CURRENT_SEASON_AREAS),
    map(action => action.payload),
    withLatestFrom(
      state$.pipe(map(state => state.entities.property.propertyData)),
      state$.pipe(map(state => state.entities.property.entities))
    ),
    concatMap(([payload, propertyData, properties]) => {
      const seasons = properties[payload.propertyId]?.seasons || [];
      const seasonsIds = filterSeasonsActive(seasons).map(season => season.id);
      return getSeasonAreasBySeasons(payload.propertyId, seasonsIds).pipe(
        map(response => {
          let activeSeasonAreas = response;
          if (propertyData) {
            activeSeasonAreas = filterCurrentSeasonAreas(activeSeasonAreas, payload.date!, propertyData.timeZone);
          }
          return GetCurrentSeasonAreasSuccess({
            current: activeSeasonAreas,
            all: response,
            propertyId: payload.propertyId,
            requestId: payload.requestId
          });
        }),
        catchError((error: string) => of(GetCurrentSeasonAreasFailure(error, payload.requestId)))
      );
    })
  );

export const handleGetSeasonAreasByDate = (
  action$: ActionsObservable<ReturnType<typeof GetSeasonAreasByDate>>,
  state$: StateObservable<AppState>
) =>
  action$.pipe(
    ofType(PropertyActionsTypes.GET_SEASON_AREAS_BY_DATE),
    map(action => action.payload),
    withLatestFrom(state$.pipe(map((state: AppState) => state.entities.property.propertyData))),
    exhaustMap(([payload, propertyData]: [GetCurrentSeasonAreaPayload, Nullable<PropertyInfoAddress>]) => {
      return getCurrentSeasonAreas(payload.propertyId, payload.allowPastSeasons, payload.date).pipe(
        map(response => {
          let seasonAreas = response;
          if (propertyData) {
            seasonAreas = filterCurrentSeasonAreas(seasonAreas, payload.date!, propertyData.timeZone);
          }
          return GetSeasonAreasByDateSuccess({ date: payload.date, content: seasonAreas });
        }),
        catchError((error: string) => of(GetSeasonAreasByDateFailure(error)))
      );
    })
  );
